import * as React from 'react'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import { useToggle } from 'usehooks-ts'

import { ExplotableItem, ExplotableList } from 'modules/core/components/GroupingExplotable'
import Modal from 'modules/core/components/Modal'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { VonqCampaignStatusTag } from 'modules/ats/components/VonqCampaignStatusTag'
import {
  CampaignStatus,
  ProductDuration,
  campaignDurationPeriodLabel,
  campaignStatusLabel,
} from 'modules/ats/lib/vonq'

type TableProduct = {
  id: string
  name: string
  status: CampaignStatus
  publishedAt: string | null
  durationPeriod: ProductDuration
  visits: number
}

interface Campaign {
  name: string
  id: string
  status: CampaignStatus
  products: TableProduct[]
}

interface Props {
  campaigns: Campaign[]
}

export const VonqCampaignsTable = ({ campaigns }: Props) => {
  return (
    <Table>
      <Table noRadius hideBorder>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell colSpan={3}>
              {i18n.t('ats.vonq.campaigns_list.job_board')}
            </Table.HeadCell>
            <Table.HeadCell wide>{i18n.t('ats.vonq.campaigns_list.published_at')}</Table.HeadCell>
            <Table.HeadCell>{i18n.t('ats.vonq.campaigns_list.duration')}</Table.HeadCell>
            <Table.HeadCell>{i18n.t('ats.vonq.campaigns_list.visits')}</Table.HeadCell>
            <Table.HeadCell wide>{i18n.t('ats.vonq.campaigns_list.status')}</Table.HeadCell>
          </Table.Row>
        </Table.Head>
      </Table>

      <Table noRadius hideBorder>
        <Table.Body>
          <ExplotableList>
            {campaigns.map((campaign) => (
              <ProductsGroup
                key={campaign.id}
                products={campaign.products}
                campaignName={campaign.name}
                campaignId={campaign.id}
                campaignStatus={campaign.status}
              />
            ))}
          </ExplotableList>
        </Table.Body>
      </Table>
    </Table>
  )
}

interface ProductsGroupProps {
  products: TableProduct[]
  campaignId: string
  campaignName: string
  campaignStatus: CampaignStatus
}

function useCancelCampaign() {
  const [isCancelling, setIsCancelling] = React.useState(false)

  const cancelCampaign = async (campaignId: string) => {
    setIsCancelling(true)

    await window.hapi.campaign.service.setCampaignCancellation.run(campaignId, {
      status: 'offline',
    })

    setIsCancelling(false)
  }

  return {
    cancelCampaign,
    isCancelling,
  }
}

const ProductsGroup = ({
  products,
  campaignName,
  campaignId,
  campaignStatus,
}: ProductsGroupProps) => {
  const [cancelCampaignModalOpen, setCancelCampaignModalOpen] = React.useState(false)
  const [open, toggle] = useToggle(true)
  const { cancelCampaign, isCancelling } = useCancelCampaign()

  return (
    <ExplotableItem
      bottomRadius={false}
      dropDownAnimated={false}
      expandIcon="caret"
      headerBorders={false}
      headerRounded={false}
      opened={open}
      renderHeader={() => (
        <Box flexDirection="row" alignItems="center" gap="s16">
          <Text.Normal size="200" weight="medium" color="grey800">
            {campaignName} ({campaignStatusLabel(campaignStatus)})
          </Text.Normal>

          {campaignStatus !== 'offline' && (
            <Button
              type="secondary"
              disabled={isCancelling}
              onClick={(event) => {
                event.stopPropagation()
                setCancelCampaignModalOpen(true)
              }}
            >
              {i18n.t('ats.vonq.campaigns_list.cancel')}
            </Button>
          )}
        </Box>
      )}
      scrollLimited={false}
      size="small"
      toggle={toggle}
      type="primary"
    >
      <Table noRadius hideBorder fullWidth>
        {products.map((product) => (
          <Table.Row key={product.id}>
            <Table.Cell colSpan={3}>{product.name}</Table.Cell>
            <Table.Cell>
              {product.publishedAt
                ? convertDate(product.publishedAt, 'backend_datetime', 'ui_short_with_time')
                : '-'}
            </Table.Cell>
            <Table.Cell wide>{campaignDurationPeriodLabel(product.durationPeriod)} </Table.Cell>
            <Table.Cell>{product.visits}</Table.Cell>
            <Table.Cell wide>
              <VonqCampaignStatusTag status={product.status} />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table>

      {cancelCampaignModalOpen && (
        <Modal
          title={i18n.t('ats.vonq.campaigns_list.cancel_modal.title')}
          description={i18n.t('ats.vonq.campaigns_list.cancel_modal.description')}
          illustration="delete"
          onClose={() => setCancelCampaignModalOpen(false)}
          onSubmit={() => cancelCampaign(campaignId)}
          footer={
            <Button type="negative" disabled={isCancelling} submit>
              {i18n.t('ats.vonq.campaigns_list.cancel_modal.submit')}
            </Button>
          }
        />
      )}
    </ExplotableItem>
  )
}
